/**
 * 运行时配置
 * 全局初始状态、布局、请求配置、主题切换
 */
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useAntdConfigSetter, useModel } from '@umijs/max';
import React, { useEffect } from 'react';
import { Switch, Tooltip, theme } from 'antd';
import { MoonOutlined, SunOutlined } from '@ant-design/icons';
import { clearToken } from './requestErrorConfig';
import { getAdminInfo } from '@/services/admin/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/user/login';

/** 从 localStorage 读取初始主题 */
function getInitialTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem('admin_theme_mode');
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // ignore
  }
  return 'light';
}

/** 根据主题模式生成 layout settings */
function buildSettings(mode: 'light' | 'dark'): Partial<LayoutSettings> {
  return {
    ...defaultSettings,
    navTheme: mode,
  };
}

/**
 * 全局初始状态
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.AdminUser;
  fetchUserInfo?: () => Promise<API.AdminUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const data = await getAdminInfo();
      return data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const initialTheme = getInitialTheme();

  // 非登录页面才获取用户信息
  const { location } = history;
  if (!location.pathname.startsWith('/user')) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: buildSettings(initialTheme),
    };
  }
  return {
    fetchUserInfo,
    settings: buildSettings(initialTheme),
  };
}

/**
 * 主题切换按钮组件
 * 同时控制 Ant Design 算法和 ProLayout navTheme
 */
const ThemeToggle: React.FC = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const { themeMode, toggleTheme, isDark } = useModel('theme');
  const setAntdConfig = useAntdConfigSetter();

  // 主题变更时同步 Ant Design 算法和 ProLayout navTheme
  useEffect(() => {
    setAntdConfig({
      theme: {
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      },
    });
    setInitialState((s) => ({
      ...s,
      settings: buildSettings(isDark ? 'dark' : 'light'),
    }));
  }, [isDark]);

  return (
    <Tooltip title={isDark ? '切换到亮色模式' : '切换到暗色模式'}>
      <Switch
        checked={isDark}
        onChange={toggleTheme}
        checkedChildren={<MoonOutlined />}
        unCheckedChildren={<SunOutlined />}
      />
    </Tooltip>
  );
};

/**
 * ProLayout 配置
 */
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<ThemeToggle key="theme-toggle" />],
    avatarProps: {
      title: initialState?.currentUser?.display_name || initialState?.currentUser?.username,
      render: (_: any, avatarChildren: React.ReactNode) => {
        return <>{avatarChildren}</>;
      },
    },
    onPageChange: () => {
      const { location } = history;
      // 未登录则跳转登录页
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    menuHeaderRender: undefined,
    // 退出登录
    menuItemRender: (item, dom) => (
      <a
        onClick={() => {
          if (item.path === '/user/logout') {
            clearToken();
            setInitialState((s) => ({ ...s, currentUser: undefined }));
            history.push(loginPath);
            return;
          }
          if (item.path) {
            history.push(item.path);
          }
        }}
      >
        {dom}
      </a>
    ),
    ...initialState?.settings,
  };
};

/**
 * 请求配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
